import React from 'react';
import { RefreshCw, WifiOff } from 'lucide-react';

interface RouteErrorBoundaryProps {
  children: React.ReactNode;
  /** When this value changes (e.g. the pathname) a shown error is cleared so navigation recovers. */
  resetKey?: string;
  /** Fill the viewport (top-level boundary) instead of the shell content area. */
  fullScreen?: boolean;
}

interface RouteErrorBoundaryState {
  error: Error | null;
}

/**
 * FND-08: a failed lazy chunk (offline cold start, stale deploy) or a render error must never leave a
 * blank page. Shows a Persian message with a retry button.
 */
export class RouteErrorBoundary extends React.Component<RouteErrorBoundaryProps, RouteErrorBoundaryState> {
  state: RouteErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error) {
    console.error('Route render failed:', error);
  }

  componentDidUpdate(prev: RouteErrorBoundaryProps) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  // React.lazy caches a rejected import for the lifetime of the module, so a real retry needs a reload.
  private handleRetry = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;
    const offline = typeof navigator !== 'undefined' && !navigator.onLine;
    return (
      <div
        data-testid="route-error-boundary"
        role="alert"
        dir="rtl"
        className={`${
          this.props.fullScreen ? 'min-h-screen' : 'min-h-[60vh]'
        } flex flex-col items-center justify-center gap-4 p-8 text-center bg-base-100 text-base-content`}
      >
        <WifiOff className="size-10 text-warning" aria-hidden="true" />
        <h1 className="text-xl font-bold">
          {offline ? 'این صفحه هنوز روی دستگاه ذخیره نشده است' : 'بارگذاری صفحه با خطا روبه‌رو شد'}
        </h1>
        <p className="text-sm text-base-content/70 max-w-sm leading-relaxed">
          {offline
            ? 'اتصال اینترنت برقرار نیست. پس از اتصال دوباره تلاش کنید.'
            : 'لطفاً دوباره تلاش کنید. اگر مشکل ادامه داشت، اتصال اینترنت خود را بررسی کنید.'}
        </p>
        <button
          type="button"
          data-testid="route-error-retry"
          onClick={this.handleRetry}
          className="h-11 px-5 rounded-xl bg-primary-fill text-white text-sm font-semibold inline-flex items-center gap-2 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-base-100"
        >
          <RefreshCw className="size-4" aria-hidden="true" />
          تلاش دوباره
        </button>
      </div>
    );
  }
}

export default RouteErrorBoundary;
